import { useEffect, useState } from 'react'
import BottomSheet from '../BottomSheet'
import { providersAPI } from '../../services/api'
import { withOriginMiles } from '../../utils/directoryOffice'
import { officeConfirmed, officeMilesText, officeStreet, officeCityLine, withOffice, splitFarOffices, defaultNearOfficeIndex } from '../../utils/officeAddress'
import { inviteClinicianMailto } from '../../utils/inviteClinician'
import { OrellaSpinner } from '../OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BODY = '#5A7185'
const MUTE = '#6B7F90'

/**
 * OfficePickerSheet — s110 (mockup "Orella Directory Office Addresses", screen 3, approved 2026-10-16).
 *
 * A directory doctor with more than one office: "Which office should get your case?". The offices come
 * from GET /providers/<npi>/offices (nearest first; re-sorted on the phone from the patient's point like the
 * list). The nearest office with a fax on file is selected first (his default). Choosing hands the caller the
 * doctor's row WITH that office (utils/officeAddress withOffice), and the caller carries on exactly as it did
 * for a one-office doctor. A list that cannot be read hands back the row as it was: the patient is never stuck.
 */
export default function OfficePickerSheet({ provider, zip, origin = null, onChoose, onClose, t }) {
  const [state, setState] = useState({ loading: true, offices: [] })
  const [sel, setSel] = useState(-1)
  const [showFar, setShowFar] = useState(false) // s126 note 2: offices > 50 mi start closed

  useEffect(() => {
    let alive = true
    providersAPI.offices(provider.npi, zip)
      .then((r) => {
        if (!alive) return
        const offices = withOriginMiles(Array.isArray(r?.offices) ? r.offices : [], origin)
        if (offices.length <= 1) { onChoose(offices.length ? withOffice(provider, offices[0]) : provider); return }
        setState({ loading: false, offices })
        setSel(defaultNearOfficeIndex(offices))
      })
      .catch(() => { if (alive) onChoose(provider) })
    return () => { alive = false }
  }, [provider.npi]) // eslint-disable-line react-hooks/exhaustive-deps

  const initials = String(provider.name || '').replace(/^dr\.?\s+/i, '').split(/\s+/).filter(Boolean)
  const ini = ((initials[0] || '')[0] || '').toUpperCase() + ((initials[initials.length - 1] || '')[0] || '').toUpperCase()
  return (
    <BottomSheet open onClose={onClose} maxHeight="88dvh">
      <div className="px-4 pb-6 flex flex-col gap-3.5" data-testid="office-picker">
        <div className="flex gap-3 items-center">
          <span className="w-11 h-11 rounded-full grid place-items-center flex-none text-[14px] font-extrabold" style={{ background: '#DCEBF7', color: NAVY }}>{ini}</span>
          <span className="min-w-0">
            <b className="block text-[16px] font-extrabold" style={{ color: NAVY }}>{provider.name}{provider.credential ? `, ${provider.credential}` : ''}</b>
            {provider.specialty && <small className="block text-[12.5px]" style={{ color: BODY }}>{provider.specialty}</small>}
          </span>
          {/* s126 (TestFlight build 92 note 8): a clinician who is not on Orella can be invited from here. */}
          {!provider.onOrella && (
            <button type="button" data-cta="office-picker-invite" onClick={() => { window.location.href = inviteClinicianMailto() }}
                    className="ml-auto flex-none rounded-full px-3 py-1.5 text-[12px] font-bold bg-white"
                    style={{ color: NAVY, border: '1.5px solid #A7D9FC' }}>
              {t('findDoctor.inviteToOrella', 'Invite to Orella')}
            </button>
          )}
        </div>
        {state.loading ? (
          <div className="flex justify-center py-6"><OrellaSpinner size={20} /></div>
        ) : (
          <>
            <div>
              <h2 className="text-[17px] font-extrabold" style={{ color: NAVY }}>{t('findDoctor.pickOfficeTitle', 'Which office should get your case?')}</h2>
              <p className="text-[12.5px] leading-snug mt-1" style={{ color: BODY }}>
                {t('findDoctor.pickOfficeBody', { defaultValue: '{{name}} sees patients at {{n}} offices. Pick the one where you have, or want, your appointment.', name: provider.name, n: state.offices.length })}
              </p>
            </div>
            <div className="flex flex-col gap-2" role="radiogroup" aria-label={t('findDoctor.pickOfficeTitle', 'Which office should get your case?')}>
              {(() => {
                const { near, far } = splitFarOffices(state.offices)
                const row = (o) => {
                const i = state.offices.indexOf(o)
                const on = i === sel
                const street = officeStreet(o)
                return (
                  <label key={i} data-cta="office-picker-row" className="flex gap-3 items-start rounded-2xl p-3 cursor-pointer"
                         style={on ? { border: '1.5px solid #46A8F9', background: '#F5FBFF' } : { border: '1px solid #E5EDF3', background: '#fff' }}>
                    <input type="radio" name="office" checked={on} onChange={() => setSel(i)} className="w-5 h-5 mt-0.5 flex-none" style={{ accentColor: NAVY }} />
                    <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                      {o.practice && <b className="text-[14px] font-bold" style={{ color: NAVY }}>{o.practice}</b>}
                      {street && <small className="text-[12.5px]" style={{ color: '#3E5568' }}>{street}</small>}
                      <small className="text-[12.5px]" style={{ color: '#3E5568' }}>{officeCityLine(o)}</small>
                      <span className="flex gap-1.5 mt-1 flex-wrap">
                        <span className="text-[11px] font-bold rounded-full px-2 py-0.5"
                              style={o.faxOnFile ? { color: BODY, background: '#F2F6FA' } : { color: '#9A4A07', background: '#FFF4E2' }}>
                          {o.faxOnFile ? t('findDoctor.faxOnFile', 'Fax on file') : t('findDoctor.noFax', 'No fax listed')}
                        </span>
                        {!officeConfirmed(o) && (
                          <span className="text-[11px] font-bold rounded-full px-2 py-0.5" style={{ color: '#9A4A07', background: '#FFF4E2' }}>
                            {t('findDoctor.addressUnconfirmed', 'Address not confirmed')}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="text-[12px] font-bold flex-none" style={{ color: MUTE }}>{officeMilesText(o, t)}</span>
                  </label>
                )
                }
                return (
                  <>
                    {near.map(row)}
                    {far.length > 0 && (
                      <button type="button" data-cta="office-picker-far" aria-expanded={showFar} onClick={() => setShowFar((v) => !v)}
                              className="flex items-center justify-between rounded-2xl px-3 py-2.5 text-[13px] font-bold"
                              style={{ color: NAVY, background: '#F2F6FA' }}>
                        <span>{t('findDoctor.fartherAway', { defaultValue: 'Farther away · {{n}}', n: far.length })}</span>
                        <span aria-hidden="true">{showFar ? '−' : '+'}</span>
                      </button>
                    )}
                    {showFar && far.map(row)}
                  </>
                )
              })()}
            </div>
            <button type="button" data-cta="office-picker-send" disabled={sel < 0}
                    onClick={() => sel >= 0 && onChoose(withOffice(provider, state.offices[sel]))}
                    className="w-full h-12 rounded-full text-white text-[14.5px] font-bold disabled:opacity-50" style={{ background: NAVY }}>
              {t('findDoctor.sendToThisOffice', 'Send my Case Report to this office')}
            </button>
          </>
        )}
      </div>
    </BottomSheet>
  )
}
